import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { AlertTriangle, Clock, MapPin, Loader2 } from 'lucide-react';
import { formatDistance } from '@/lib/locationUtils';
import { format } from 'date-fns';

interface AttendanceReasonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isLate: boolean;
  isOutsideRange: boolean;
  distance?: number;
  allowedRadius?: number;
  checkInTime: Date;
  onConfirm: (lateReason: string, locationReason: string) => Promise<void>;
}

export function AttendanceReasonDialog({
  open,
  onOpenChange,
  isLate,
  isOutsideRange,
  distance,
  allowedRadius,
  checkInTime,
  onConfirm,
}: AttendanceReasonDialogProps) {
  const [lateReason, setLateReason] = useState('');
  const [locationReason, setLocationReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setLateReason('');
      setLocationReason('');
    }
  }, [open]);

  const handleSubmit = async () => {
    if (isLate && !lateReason.trim()) return;
    if (isOutsideRange && !locationReason.trim()) return;

    setSubmitting(true);
    try {
      await onConfirm(lateReason.trim(), locationReason.trim());
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to submit attendance:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
    setLateReason('');
    setLocationReason('');
  };

  const getTitle = () => {
    if (isLate && isOutsideRange) return 'Verification Required';
    if (isLate) return 'Late Arrival';
    if (isOutsideRange) return 'Outside Office Range';
    return 'Attendance Confirmation';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px] p-0 gap-0 overflow-hidden">
        {/* Header */}
        <DialogHeader className="px-5 pt-5 pb-3">
          <DialogTitle className="flex items-center gap-2 text-base pr-6">
            <AlertTriangle className="h-4 w-4 text-yellow-600 shrink-0" />
            {getTitle()}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {isLate && isOutsideRange
              ? 'Please provide reasons for both conditions.'
              : isLate
                ? 'Please provide a reason for late arrival.'
                : 'Please provide a reason for remote check-in.'}
          </DialogDescription>
        </DialogHeader>

        <div className="px-5 pb-4 space-y-3">
          {/* Clubbed Info Bar */}
          {(isLate || isOutsideRange) && (
            <div className="flex items-center gap-2 text-xs rounded-lg bg-yellow-50 border border-yellow-200 px-3 py-2">
              {isLate && (
                <span className="flex items-center gap-1 text-yellow-800">
                  <Clock className="h-3.5 w-3.5 text-yellow-600" />
                  {format(checkInTime, 'hh:mm a')}
                </span>
              )}
              {isLate && isOutsideRange && (
                <span className="text-yellow-300">|</span>
              )}
              {isOutsideRange && distance !== undefined && allowedRadius !== undefined && (
                <span className="flex items-center gap-1 text-orange-800">
                  <MapPin className="h-3.5 w-3.5 text-orange-600" />
                  {formatDistance(distance)} away (Limit {formatDistance(allowedRadius)})
                </span>
              )}
            </div>
          )}

          {/* Late Reason Field */}
          {isLate && (
            <div className="space-y-1.5">
              <Label htmlFor="lateReason" className="text-xs font-semibold">
                Reason for Late Arrival <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="lateReason"
                placeholder="Explain reason for late arrival..."
                value={lateReason}
                onChange={(e) => setLateReason(e.target.value)}
                rows={2}
                className="resize-none text-sm min-h-0"
                required
                autoFocus
              />
              {!lateReason.trim() && (
                <p className="text-[11px] text-red-500">Late arrival reason is required</p>
              )}
            </div>
          )}

          {/* Location Reason Field */}
          {isOutsideRange && (
            <div className="space-y-1.5">
              <Label htmlFor="locationReason" className="text-xs font-semibold">
                Reason for Remote Location <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="locationReason"
                placeholder="e.g., field work, client meeting..."
                value={locationReason}
                onChange={(e) => setLocationReason(e.target.value)}
                rows={2}
                className="resize-none text-sm min-h-0"
                required
              />
              {!locationReason.trim() && (
                <p className="text-[11px] text-red-500">Location reason is required</p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 flex flex-col gap-2">
          <Button
            type="submit"
            onClick={handleSubmit}
            disabled={
              submitting ||
              (isLate && !lateReason.trim()) ||
              (isOutsideRange && !locationReason.trim())
            }
            className="w-full h-11 bg-green-600 hover:bg-green-700 text-base font-semibold"
          >
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Confirm Attendance
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={handleCancel}
            disabled={submitting}
            className="w-full text-muted-foreground"
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
